import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Home, Search, Radio, Bookmark, User, Film, Menu, X, ChevronUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PRIMARY = [
  { to: "/", icon: Home, label: "Home" },
  { to: "/watch", icon: Film, label: "Watch" },
  { to: "/search", icon: Search, label: "Search" },
  { to: "/live", icon: Radio, label: "Live" },
];

/** Every nav item surfaced in the bottom sheet when the user taps "More". */
const SHEET_GROUPS = [
  { title: "Browse", items: [
    { to: "/tv", label: "TV App" },
    { to: "/browse/films", label: "Movies" },
    { to: "/browse/music", label: "Music" },
    { to: "/shorts", label: "Stories / Shorts" },
    { to: "/browse/news", label: "News" },
    { to: "/browse/podcasts", label: "Podcasts" },
    { to: "/shop", label: "Shop" },
    { to: "/kids", label: "Kids" },
  ]},
  { title: "You", items: [
    { to: "/timeline", label: "My Timeline" },
    { to: "/profile/edit", label: "Edit Profile" },
    { to: "/dashboard", label: "My Dashboard" },
    { to: "/settings/notifications", label: "Notifications" },
    { to: "/orders", label: "Orders" },
    { to: "/following", label: "Following" },
  ]},
  { title: "Connect", items: [
    { to: "/community", label: "Community Wall" },
    { to: "/awards", label: "$50K Awards" },
    { to: "/contact", label: "Contact / Support" },
  ]},
  { title: "Pro", items: [
    { to: "/studio", label: "Creator Studio", roles: ["creator", "premium_creator", "admin"] },
    { to: "/studio/live", label: "Director's Mode", roles: ["premium_creator", "admin"] },
    { to: "/conference", label: "Conferences", roles: ["premium_creator", "admin"] },
    { to: "/admin", label: "Admin Console", roles: ["admin"] },
  ]},
];

export default function BottomNav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const role = user?.role;

  const go = (to) => {
    setOpen(false);
    nav(to);
  };

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-black/95 backdrop-blur-md border-t border-white/10"
      >
        <div className="flex items-center justify-around px-2 py-2">
          {PRIMARY.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition ${isActive ? "text-tv-accent" : "text-white/70"}`
                }
                data-testid={`bottom-nav-${it.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-semibold">{it.label}</span>
              </NavLink>
            );
          })}
          <button
            onClick={() => setOpen(true)}
            data-testid="bottom-nav-more"
            className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-white/70 hover:text-tv-accent"
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px] font-semibold">More</span>
          </button>
        </div>
      </nav>

      {/* Sliding bottom sheet */}
      <div
        aria-hidden={!open}
        onClick={() => setOpen(false)}
        className={`md:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid="bottom-nav-sheet"
        role="dialog"
        aria-modal="true"
        className={`md:hidden fixed inset-x-0 bottom-0 z-[55] bg-[#0B1120] border-t border-white/15 rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out ${open ? "translate-y-0" : "translate-y-full"}`}
        style={{ maxHeight: "85vh" }}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" aria-label="Close menu" data-testid="bottom-nav-sheet-close">
            <X className="w-5 h-5" />
          </button>
          <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold">Tap In TV Menu</div>
          <ChevronUp className="w-5 h-5 text-white/30" />
        </div>
        <div className="px-5 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] pt-2 overflow-y-auto" style={{ maxHeight: "calc(85vh - 60px)" }}>
          {SHEET_GROUPS.map((group) => {
            const items = group.items.filter((i) => !i.roles || i.roles.includes(role));
            if (items.length === 0) return null;
            return (
              <div key={group.title} className="mt-4 first:mt-0">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">{group.title}</div>
                <div className="grid grid-cols-2 gap-2">
                  {items.map((i) => (
                    <button
                      key={i.to}
                      onClick={() => go(i.to)}
                      data-testid={`sheet-nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm font-semibold"
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {!user && (
            <Link to="/login" onClick={() => setOpen(false)} className="mt-6 block bg-tv-accent text-black font-bold text-center py-3 rounded-full" data-testid="sheet-signin">
              Sign in
            </Link>
          )}
        </div>
      </aside>
    </>
  );
}
